import React, { useCallback, useMemo, useRef, useEffect } from 'react'

import { useRouter } from 'next/router'
import ForecastsClient from '@avcan/utils/api/clients/forecasts'

import * as Config from 'clients/config'
import { STYLES } from 'services/mapbox/config'
import * as MapState from 'contexts/map/state'
import * as MapContext from 'contexts/map'
import { Place } from 'components/icons'
import { PRIMARY } from 'constants/colors'
import * as ProductUtils from 'utils/product'
import * as Sources from './sources/context'
import { formatDateForQuery } from 'utils/product'
import styles from './Map.module.css'
import { ArchiveForecastAreas } from './ArchiveForecastAreas'

const env = process.env.NODE_ENV

const client = new ForecastsClient(Config.api, Config.key)

export default function ArchiveMap({ children }) {
    const { query, locale, push } = useRouter()
    const { lat, lng, date: dateParam } = query || {}
    const formattedDate = formatDateForQuery(
        dateParam ? dateParam : new Date().toISOString().substring(0, 10)
    )
    const { productId, setProductId, openPrimaryDrawer } = MapState.useMapState()
    const { zoom, center, errors } = MapState.useMapState()
    const options = useRef()
    const areas = Sources.useForecastAreas()

    // Set the product via an API call rather than battling async issues (like waiting for the map to load then clicking on a layer)
    useEffect(() => {
        if (lat && lng) {
            client.pointByDate({ lat, lng }, formattedDate, locale).then(response => {
                if (!response.id) {
                    return
                }
                setProductId(response.id)
                openPrimaryDrawer()
            })
        }
    }, [formattedDate, lat, lng, locale, openPrimaryDrawer, setProductId])

    options.current = MapState.useOptions(options)

    const once = useMemo(
        () => ({
            async load(event) {
                const { target: map } = event
                if (env === 'development') {
                    window.map = map
                }
                if (lng && lat) {
                    try {
                        // Zoom to the point on page load. Note that this is slightly different than zooming to the corresponding forecast’s polygon bounds (got into nasty async issues)
                        map.flyTo({
                            center: [lng, lat],
                            zoom: 6,
                        })
                    } catch (error) {
                        // We do not need to do anything with that!
                        // Required to avoid "onunhandledrejection" error!
                    }
                }
            },
        }),
        []
    )

    const on = useMemo(
        () => ({
            zoomend(event) {
                const value = event.target.getZoom()

                zoom.set(value)
            },
            moveend(event) {
                const value = event.target.getCenter()

                center.set(value)
            },
            error(event) {
                errors.add(MapState.ERRORS.MAP, event.error)
            },
        }),
        []
    )

    const onLayerClick = useCallback(
        event => {
            const { point, lngLat, target: map } = event
            const features = map.queryRenderedFeatures(point)

            if (features.length === 0) {
                return
            }
            const [feature] = features
            const { properties } = feature

            const { product, id } = properties
            const path = ProductUtils.createPath(product, id)

            if (ProductUtils.isAnalysis(product)) {
                const { lat, lng } = lngLat

                push(
                    {
                        query: {
                            ...query,
                            lat: lat.toFixed(6),
                            lng: lng.toFixed(6),
                        },
                    },
                    undefined,
                    {
                        shallow: true,
                    }
                )

                setProductId(id)
                openPrimaryDrawer()
            } else {
                url.searchParams.set('panel', path)
                url.searchParams.delete('lat')
                url.searchParams.delete('lng')
            }

            const polygon = areas?.features.find(area => {
                return area.properties.id === feature.id
            })

            zoomToPolygon(map, polygon)
        },
        [areas?.features, openPrimaryDrawer, push, query, setProductId]
    )

    return (
        <div className={styles.Map} data-test="archiveMap">
            <MapContext.Map options={options.current} style={STYLES.grayscale} on={on} once={once}>
                <MapContext.NavigationControl />
                <MapContext.GeolocateControl options={GEOLOCATE_CONTROL_OPTIONS} />
                <MapContext.WithMapReady>
                    <ArchiveForecastAreas on={onLayerClick} productId={productId} />
                </MapContext.WithMapReady>
                {children}
                {lat && lng && (
                    <MapContext.Marker
                        lnglat={{
                            lng,
                            lat,
                        }}>
                        <Place width={40} height={40} color={PRIMARY} />
                    </MapContext.Marker>
                )}
            </MapContext.Map>
        </div>
    )
}

// Helpers and Constants
const GEOLOCATE_CONTROL_OPTIONS = {
    fitBoundsOptions: {
        maxZoom: 10,
    },
}

const zoomToPolygon = (map, polygon) => {
    if (!polygon) {
        return null
    }
    map.fitBounds(polygon.bbox, {
        padding: {
            top: 50,
            bottom: 50,
            left: 50,
            // Large right padding to offset the map when the right drawer is open.
            // As a side effect, this also prevents zoom-to-fit on mobile, which is desirable as the drawer completely covers the map once opened.
            right: 500,
        },
    })
}
